import {
  type ReadinessCheck,
  type ReadinessStatus,
  useTenantReadiness,
} from '@/api/hooks/useTenantReadiness'
import { formatTimestamp } from '@/utils/dateFormatters'
import { Alert, Button, Result, Space, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'

const STATUS_TAG_COLOR: Record<ReadinessStatus, string> = {
  ok: 'success',
  warn: 'warning',
  fail: 'error',
  skipped: 'default',
}

const RESULT_STATUS = {
  ok: 'success',
  warn: 'warning',
  fail: 'error',
} as const

interface TenantHealthProps {
  tenantName: string | undefined
}

export function TenantHealth({ tenantName }: TenantHealthProps) {
  const { data, error, isFetching, refetch } = useTenantReadiness(tenantName)

  if (!tenantName) {
    return <Alert type='info' message='Select a tenant to view its health.' />
  }

  if (error) {
    return (
      <Alert
        type='error'
        message='Failed to load readiness'
        description={error instanceof Error ? error.message : String(error)}
        action={<Button onClick={() => refetch()}>Retry</Button>}
      />
    )
  }

  if (!data) {
    return <Alert type='info' message='Loading…' />
  }

  const columns: ColumnsType<ReadinessCheck> = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: ReadinessStatus) => (
        <Tag color={STATUS_TAG_COLOR[status]}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Check',
      dataIndex: 'label',
      key: 'label',
      width: 280,
    },
    {
      title: 'Detail',
      dataIndex: 'detail',
      key: 'detail',
      render: (detail: string | null) =>
        detail ? (
          <span style={{ color: 'var(--foreground)' }}>{detail}</span>
        ) : null,
    },
    {
      title: 'Remediation',
      dataIndex: 'remediation',
      key: 'remediation',
      render: (rem: string | null) =>
        rem ? (
          <code
            style={{
              backgroundColor: 'var(--bg-2)',
              color: 'var(--foreground)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            {rem}
          </code>
        ) : null,
    },
  ]

  return (
    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
      <Result
        status={RESULT_STATUS[data.overall]}
        title={`Overall: ${data.overall.toUpperCase()}`}
        subTitle={`Generated ${formatTimestamp(data.generated_at)}`}
        extra={
          <Button onClick={() => refetch()} loading={isFetching}>
            Re-check now
          </Button>
        }
      />
      <Table<ReadinessCheck>
        rowKey='id'
        dataSource={data.checks}
        columns={columns}
        pagination={false}
        size='middle'
      />
    </Space>
  )
}
